import React, { useState } from 'react';
import { MenuItem, Stack, TextField } from '@mui/material';

import { TextOperationService } from '../../../services';
import type { TextSelectionTarget } from '../../../domain';
import {
  DEFAULT_DISTILL_TEXT_STATE,
  buildDistillTextRequest,
  type DistillTextFormState,
} from './buildRequests';
import { useRunTextOperation } from './useTextOperationMutation';
import { TEXT_OPERATION_CATALOG } from './operationCatalog';
import { PanelScaffold } from './PanelScaffold';
import type { TextPanelProps } from './panelProps';

const PLACEMENTS: { value: TextSelectionTarget; label: string }[] = [
  { value: 'ReplaceDocument', label: 'Replace document' },
  { value: 'TopOfDocument', label: 'Insert at top' },
  { value: 'BottomOfDocument', label: 'Insert at bottom' },
];

export const DistillPanel: React.FC<TextPanelProps> = ({
  ensureSession,
  isCreatingSession = false,
  documentId,
  sectionId,
  onResult,
}) => {
  const [state, setState] = useState<DistillTextFormState>(DEFAULT_DISTILL_TEXT_STATE);
  const { run, mutation, validationError } = useRunTextOperation(
    TextOperationService.distill,
    ensureSession,
    documentId,
    { onResult },
  );

  const needsDocument = !documentId;
  const handleSubmit = () => run((sid, docId) => buildDistillTextRequest(state, sid, docId, sectionId));

  return (
    <PanelScaffold
      meta={TEXT_OPERATION_CATALOG.Distill}
      needsDocument={needsDocument}
      validationError={validationError}
      mutationError={mutation.isError ? mutation.error : null}
      busy={mutation.isPending}
      starting={isCreatingSession}
      submitDisabled={mutation.isPending || isCreatingSession || needsDocument}
      submitLabel="Distill"
      busyLabel="Distilling…"
      onSubmit={handleSubmit}
    >
      <Stack direction="row" spacing={1}>
        <TextField
          label="Target words"
          type="number"
          value={state.targetWordCount}
          onChange={e => setState(s => ({ ...s, targetWordCount: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 50, max: 2000, step: 25 }}
          // Shares the row with Placement, so it takes an equal share rather
          // than shrinking to the number input's content width and clipping
          // the "Target words" label.
          sx={{ flex: 1, minWidth: 150 }}
        />
        <TextField
          select
          label="Placement"
          value={state.placement}
          onChange={e =>
            setState(s => ({ ...s, placement: e.target.value as TextSelectionTarget }))
          }
          size="small"
          // `flex: 1` rather than fullWidth: in this flex row a 100%-width
          // sibling would crowd "Target words" back out.
          sx={{ flex: 1, minWidth: 140 }}
        >
          {PLACEMENTS.map(p => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </PanelScaffold>
  );
};
